import React, { useRef } from "react";
import { ButtonGroup, Tooltip } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  getGridClickBoundaryProps,
  stopGridClickPropagation,
} from "../Grid/GridClickBoundary";
import type { OrderedRowAction } from "./orderedRowActions";
import { ColoredTooltip } from "../shared/Tooltip/ColoredTooltip";
import {
  EditOrderTooltipText,
  NewReqActionButtonSlot,
  NewReqActionTooltip,
  NewReqButtonGroupWrapper,
  NewReqHoverButton,
  NewReqOverflowMenu,
  NewReqOverflowMenuItem,
} from "./NewReqNameAndActionsCell.styles";

export const ACTION_TOOLTIP_PROPS = {
  arrow: true,
  placement: "top" as const,
  enterDelay: 500,
  leaveDelay: 0,
  disableInteractive: true,
};

type OrderedRowActionBarProps = {
  orderId: string;
  directActions: OrderedRowAction[];
  overflowActions: OrderedRowAction[];
  hasOverflow: boolean;
  overflowAnchor: HTMLButtonElement | null;
  onOpenOverflow: (anchor: HTMLButtonElement) => void;
  onCloseOverflow: () => void;
};

export const OrderedRowActionBar: React.FC<OrderedRowActionBarProps> = ({
  orderId,
  directActions,
  overflowActions,
  hasOverflow,
  overflowAnchor,
  onOpenOverflow,
  onCloseOverflow,
}) => {
  const isOverflowOpen = Boolean(overflowAnchor);
  const actionButtonCount = directActions.length + (hasOverflow ? 1 : 0);
  const triggerId = `ordered-actions-trigger-${orderId}`;
  const menuId = `ordered-actions-menu-${orderId}`;
  const overflowTriggerRef = useRef<HTMLButtonElement | null>(null);

  /**
   * Hand focus back before the menu closes, while the bar is still on screen —
   * a hidden bar cannot take focus. The bar then stays up only if the browser
   * treats that focus as keyboard-driven (:focus-visible), so pointer closes
   * dismiss it and keyboard closes do not strand the user.
   */
  const closeOverflow = () => {
    overflowTriggerRef.current?.focus();
    onCloseOverflow();
  };

  const handleOverflowSelect = (
    event: React.MouseEvent<HTMLLIElement>,
    action: OrderedRowAction,
  ) => {
    event.stopPropagation();
    if (action.disabled) return;
    closeOverflow();
    action.onSelect();
  };

  const renderDirectAction = (action: OrderedRowAction) => {
    const button = (
      <NewReqActionButtonSlot>
        <NewReqHoverButton
          aria-label={action.label}
          aria-disabled={action.disabled}
          disabled={action.disabled && !action.keepFocusWhenDisabled}
          $isDisabled={action.disabled}
          onClick={(event) => {
            stopGridClickPropagation(event);
            if (!action.disabled) action.onSelect();
          }}
          type="button"
        >
          {action.icon}
        </NewReqHoverButton>
      </NewReqActionButtonSlot>
    );

    if (action.tooltipBackgroundColor) {
      return (
        <ColoredTooltip
          key={action.key}
          {...ACTION_TOOLTIP_PROPS}
          backgroundColor={action.tooltipBackgroundColor}
          title={<EditOrderTooltipText>{action.label}</EditOrderTooltipText>}
        >
          {button}
        </ColoredTooltip>
      );
    }

    return (
      <Tooltip
        key={action.key}
        {...ACTION_TOOLTIP_PROPS}
        title={<NewReqActionTooltip>{action.label}</NewReqActionTooltip>}
      >
        {button}
      </Tooltip>
    );
  };

  return (
    <>
      <NewReqButtonGroupWrapper
        $actionCount={actionButtonCount}
        {...getGridClickBoundaryProps()}
      >
        <ButtonGroup variant="contained" size="small">
          {directActions.map(renderDirectAction)}
          {hasOverflow && (
            <Tooltip
              {...ACTION_TOOLTIP_PROPS}
              title={<NewReqActionTooltip>More actions</NewReqActionTooltip>}
            >
              <NewReqHoverButton
                id={triggerId}
                ref={overflowTriggerRef}
                aria-label="More actions"
                aria-haspopup="menu"
                aria-expanded={isOverflowOpen}
                aria-controls={isOverflowOpen ? menuId : undefined}
                onClick={(event) => {
                  stopGridClickPropagation(event);
                  onOpenOverflow(event.currentTarget);
                }}
                type="button"
              >
                <MoreHorizIcon />
              </NewReqHoverButton>
            </Tooltip>
          )}
        </ButtonGroup>
      </NewReqButtonGroupWrapper>
      {hasOverflow && (
        <NewReqOverflowMenu
          id={menuId}
          anchorEl={overflowAnchor}
          open={isOverflowOpen}
          onClose={closeOverflow}
          // closeOverflow owns the hand-off, so the menu must not fight it.
          disableRestoreFocus
          disableEnforceFocus
          MenuListProps={{ "aria-labelledby": triggerId }}
        >
          {overflowActions.map((action) => (
            <NewReqOverflowMenuItem
              key={action.key}
              disabled={action.disabled}
              onClick={(event) => handleOverflowSelect(event, action)}
            >
              {action.label}
            </NewReqOverflowMenuItem>
          ))}
        </NewReqOverflowMenu>
      )}
    </>
  );
};
